import { useState } from 'react';
import { CircleCheck } from 'lucide-react';

import { Panel, PanelHeader } from '@/components/ui/Panel';
import { Button } from '@/components/ui/Button';
import { DropZone } from '@/components/ui/DropZone';
import { InlineError } from '@/components/ui/EmptyState';
import { Badge } from '@/components/ui/Badge';
import { useToast } from '@/components/ui/Toast';
import { useUploadDocument } from '@/lib/mutations';
import { toMessage } from '@/lib/api';

/**
 * One document, one panel. Requirements are stated as concrete, checkable
 * conditions rather than "upload a clear photo" — most rejections come from
 * glare and cropped corners, so those are named explicitly.
 */
export function DocumentStep({
  kind,
  title,
  eyebrow,
  requirements,
  uploaded,
  locked,
}: {
  kind: 'passport' | 'selfie';
  title: string;
  eyebrow: string;
  requirements: string[];
  uploaded: boolean;
  /** True while the account is under review — resubmission is blocked. */
  locked: boolean;
}) {
  const toast = useToast();
  const upload = useUploadDocument(kind);
  const [files, setFiles] = useState<File[]>([]);
  const [error, setError] = useState<string | null>(null);

  const submit = async (): Promise<void> => {
    const file = files[0];
    if (!file) return;

    setError(null);
    try {
      await upload.mutateAsync(file);
      setFiles([]);
      // Phrased without the document's own name: «паспорт» is masculine and
      // «селфи» is neuter, so a shared template would agree with one and not
      // the other. The panel heading already says which document this is.
      toast.success('Документ загружен', 'Его проверят при рассмотрении заявки.');
    } catch (uploadError) {
      setError(toMessage(uploadError, 'Не удалось загрузить документ.'));
    }
  };

  return (
    <Panel padding="none">
      <PanelHeader
        eyebrow={eyebrow}
        title={title}
        actions={
          uploaded ? (
            <Badge tone="ok">
              <CircleCheck size={11} strokeWidth={1.5} aria-hidden="true" className="mr-1" />
              Загружен
            </Badge>
          ) : (
            <Badge>Обязателен</Badge>
          )
        }
      />

      <div className="px-4 py-4">
        <ul className="mb-4 space-y-1.5">
          {requirements.map((requirement) => (
            <li key={requirement} className="flex gap-2.5 text-sm leading-snug text-fg-secondary">
              <span
                aria-hidden="true"
                className="mt-[0.5rem] h-1 w-1 shrink-0 rounded-full bg-fg-faint"
              />
              {requirement}
            </li>
          ))}
        </ul>

        {error ? <InlineError message={error} className="mb-3" /> : null}

        <DropZone
          files={files}
          onChange={(next) => {
            setFiles(next.slice(-1));
            setError(null);
          }}
          maxFiles={1}
          // The passport is photographed with the rear lens, the selfie with
          // the front one. Tapping «Снять камерой» opens the right camera
          // straight away instead of a file browser; the gallery route stays
          // for traders who photographed the document earlier.
          capture={kind === 'passport' ? 'environment' : 'user'}
          disabled={locked || upload.isPending}
          label={uploaded ? 'Заменить загруженный файл' : 'Загрузить файл'}
          requirements={
            locked ? 'Заявка на проверке — сейчас документы изменить нельзя.' : undefined
          }
        />

        {files.length > 0 ? (
          <div className="mt-3 flex flex-wrap gap-2">
            <Button
              variant="primary"
              onClick={() => void submit()}
              isLoading={upload.isPending}
              disabled={locked}
            >
              {upload.isPending ? 'Загружаю…' : uploaded ? 'Заменить документ' : 'Загрузить документ'}
            </Button>
            <Button onClick={() => setFiles([])} disabled={upload.isPending}>
              Очистить
            </Button>
          </div>
        ) : null}
      </div>
    </Panel>
  );
}
